import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, BookMarked, CalendarDays, Check, ClipboardCheck, Loader2, RefreshCw, Sparkles, Timer,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  ASSESSMENT_TYPES, createExam, fetchCurriculum, fetchExamStudents, generatePlan,
} from '@/lib/premiumExams';
import BuddyCorner from '@/components/common/BuddyCorner';

const KIND_STYLE = {
  study: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  revision: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  final_revision: 'bg-amber-50 border-amber-300 text-amber-900',
};

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const weekday = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short' });

/**
 * ExamModeWizard — the three screens a student walks through the moment they
 * choose Exam Mode: the exam, the portion (subject → chapters → paper date),
 * then the daily routine we built from it. A pop-up, never a redirect: the
 * student never loses the page they were on.
 */
export function ExamModeWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(1);
  const [student, setStudent] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [form, setForm] = useState({ assessment_type: 'unit_test', name: '', start_date: '', end_date: '' });
  const [selected, setSelected] = useState({});      // subject → [chapters]
  const [paperDates, setPaperDates] = useState({});  // subject → yyyy-mm-dd
  const [openSubject, setOpenSubject] = useState('');
  const [exam, setExam] = useState(null);
  const [plan, setPlan] = useState(null);
  const [zoom, setZoom] = useState('');
  const [busy, setBusy] = useState(false);
  const [syllabusError, setSyllabusError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setStep(1); setExam(null); setPlan(null); setZoom(''); setSyllabusError(''); setCurriculum(null);
    let me = null;
    fetchExamStudents()
      .then(d => {
        me = (d.students || [])[0] || null;
        setStudent(me);
        return fetchCurriculum(me?.board || '', me?.class_level || '');
      })
      .then(data => {
        setCurriculum(data);
        if (!(data?.subjects || []).some(s => (s.chapters || []).length)) {
          // We have no approved book for this board + class. Say so and stop
          // spinning — the child's next step is to file their own syllabus.
          setSyllabusError(
            `We don't have the ${me?.board || 'this board'} Class ${me?.class_level || ''} syllabus yet.`);
        }
      })
      .catch(() => {
        setCurriculum({ subjects: [] });
        setSyllabusError("We couldn't load a syllabus for your board and class.");
      });
  }, [open]);

  const subjects = curriculum?.subjects || [];
  const chosen = useMemo(() => Object.keys(selected).filter(s => (selected[s] || []).length), [selected]);

  const toggleChapter = (subject, chapter) => setSelected(prev => {
    const list = prev[subject] || [];
    return { ...prev, [subject]: list.includes(chapter) ? list.filter(c => c !== chapter) : [...list, chapter] };
  });

  const selectAll = (subject, chapters) => setSelected(prev => ({
    ...prev,
    [subject]: (prev[subject] || []).length === chapters.length ? [] : chapters.map(c => c.name),
  }));

  const buildPlan = async (examId) => {
    const built = await generatePlan(examId);
    setPlan(built);
    setZoom('');
    return built;
  };

  const goStep2 = () => {
    if (!form.name.trim()) { toast.error('Give the exam a name'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Add the start and end dates'); return; }
    setStep(2);
  };

  const goStep3 = async () => {
    if (!chosen.length) { toast.error('Pick at least one subject and its chapters'); return; }
    setBusy(true);
    try {
      const saved = exam || await createExam({
        ...form,
        child_id: student?.student_id,
        subjects: chosen.map(s => ({
          subject: s,
          chapters_selected: selected[s] || [],
          chapters_mapped: selected[s] || [],
          exam_date: paperDates[s] || form.start_date,
        })),
      });
      setExam(saved);
      await buildPlan(saved.exam_id);
      setStep(3);
    } catch (e) { toast.error(e?.message || 'Could not build your plan'); }
    setBusy(false);
  };

  const regenerate = async () => {
    if (!exam) return;
    setBusy(true);
    try { await buildPlan(exam.exam_id); toast.success('Plan rebuilt'); }
    catch (e) { toast.error(e?.message || 'Could not rebuild the plan'); }
    setBusy(false);
  };

  const days = plan?.days || plan?.plan?.days || [];
  const zoomDay = days.find(d => d.date === zoom) || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="exam-mode-wizard">
        <DialogTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-fuchsia-500" />
          {step === 1 ? 'Which exam are we preparing for?'
            : step === 2 ? 'What is in the portion?'
            : 'Here is your daily routine'}
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-500 font-body">
          Step {step} of 3 · {student?.board || ''} {student?.class_level ? `Class ${student.class_level}` : ''}
          {' '}— I'll turn this into a day-by-day plan you can change any time.
        </DialogDescription>

        {/* ---------- step 1 : exam details ---------- */}
        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-testid="wizard-step-1">
            <div>
              <Label className="text-xs font-body text-slate-500">Assessment type</Label>
              <select data-testid="wizard-exam-type" value={form.assessment_type}
                onChange={e => setForm(f => ({ ...f, assessment_type: e.target.value }))}
                className="w-full mt-1 p-2 rounded-lg border border-slate-200 text-sm font-body bg-white">
                {ASSESSMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-body text-slate-500">Exam name</Label>
              <Input data-testid="wizard-exam-name" value={form.name} placeholder="Half-Yearly · September"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs font-body text-slate-500">First paper</Label>
              <Input data-testid="wizard-exam-start" type="date" value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1 rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs font-body text-slate-500">Last paper</Label>
              <Input data-testid="wizard-exam-end" type="date" value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="mt-1 rounded-lg text-sm" />
            </div>
          </div>
        )}

        {/* ---------- step 2 : subjects + chapters + paper dates ---------- */}
        {step === 2 && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3" data-testid="wizard-step-2">
            <div className="space-y-1.5">
              <p className="text-[10px] font-heading uppercase tracking-widest text-slate-400">Subjects</p>
              {!curriculum && (
                <p className="flex items-center gap-2 text-xs text-slate-400 font-body">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading your syllabus…
                </p>
              )}
              {syllabusError && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3" data-testid="wizard-no-syllabus">
                  <p className="font-heading text-[12px] text-amber-900">{syllabusError}</p>
                  <p className="font-body text-[11px] text-amber-900/90 mt-1">
                    Please upload your book under <strong>Syllabus</strong> — tap your picture at the top
                    right and choose Syllabus. Our team reviews it and builds your material.
                  </p>
                  <button type="button" data-testid="wizard-open-syllabus"
                    onClick={() => { onOpenChange(false); navigate('/syllabus'); }}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-600 text-white text-[11px] font-heading hover:bg-amber-700 transition-colors">
                    <BookMarked className="w-3.5 h-3.5" /> Open Syllabus
                  </button>
                </div>
              )}
              {subjects.map(s => {
                const count = (selected[s.name] || []).length;
                const isOpen = openSubject === s.name;
                return (
                  <button key={s.name} type="button" onClick={() => setOpenSubject(s.name)}
                    data-testid={`wizard-subject-${s.name.replace(/\s/g, '-').toLowerCase()}`}
                    className={`w-full text-left rounded-2xl border px-3 py-2.5 transition-all ${
                      isOpen ? 'border-indigo-400 bg-indigo-50' : count ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-white hover:border-indigo-200'}`}>
                    <p className="font-heading text-[13px] text-slate-800">{s.name}</p>
                    <p className="font-body text-[11px] text-slate-500">
                      {count ? `${count} chapter${count > 1 ? 's' : ''}` : 'not in portion'}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              {!openSubject ? (
                <p className="font-body text-[12px] text-slate-500">
                  Pick a subject on the left — its chapters and paper date open here.
                </p>
              ) : (
                (() => {
                  const subj = subjects.find(s => s.name === openSubject);
                  const chapters = subj?.chapters || [];
                  const sel = selected[openSubject] || [];
                  return (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-heading text-[13px] text-slate-900">{openSubject}</p>
                        <button type="button" onClick={() => selectAll(openSubject, chapters)}
                          data-testid="wizard-chapters-all"
                          className="text-[11px] font-heading text-indigo-600 hover:underline">
                          {sel.length === chapters.length ? 'Clear all' : 'Select all'}
                        </button>
                      </div>

                      <div className="mt-2">
                        <Label className="text-xs font-body text-slate-500">Paper date for {openSubject}</Label>
                        <Input type="date" data-testid="wizard-paper-date"
                          value={paperDates[openSubject] || ''}
                          min={form.start_date || undefined} max={form.end_date || undefined}
                          onChange={e => setPaperDates(p => ({ ...p, [openSubject]: e.target.value }))}
                          className="mt-1 rounded-lg text-sm" />
                        <p className="font-body text-[11px] text-slate-400 mt-1">
                          I keep the two hours before this date free for {openSubject} alone, and slot a mock a few days earlier.
                        </p>
                      </div>

                      <div className="mt-3 max-h-64 overflow-y-auto space-y-1 pr-1" data-testid="wizard-chapter-list">
                        {chapters.length === 0 && (
                          <p className="font-body text-[11px] text-slate-400">No approved chapters for this subject yet.</p>
                        )}
                        {chapters.map(c => {
                          const on = sel.includes(c.name);
                          return (
                            <button key={c.chapter_id} type="button" onClick={() => toggleChapter(openSubject, c.name)}
                              data-testid={`wizard-chapter-${c.chapter_id}`}
                              className={`w-full text-left text-[12px] font-body px-2.5 py-2 rounded-xl border transition-all ${
                                on ? 'border-indigo-300 bg-indigo-50 text-slate-900' : 'border-slate-100 text-slate-600 hover:bg-slate-50'}`}>
                              <span className={`inline-flex w-3.5 h-3.5 mr-2 rounded border items-center justify-center align-middle ${
                                on ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300'}`}>
                                {on && <Check className="w-2.5 h-2.5" />}
                              </span>
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}

        {/* ---------- step 3 : the routine ---------- */}
        {step === 3 && (
          <div className="mt-2" data-testid="wizard-step-3">
            {!plan ? (
              <p className="flex items-center gap-2 text-xs text-slate-400 font-body">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laying out your days…
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-heading px-2.5 py-1 rounded-full bg-slate-900 text-white">
                    <Timer className="w-3 h-3" /> {plan.per_day_target} min a day
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-heading px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
                    <CalendarDays className="w-3 h-3" /> {days.length} study days
                  </span>
                  {plan.revision_days > 0 && (
                    <span className="text-[11px] font-heading px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      {plan.revision_days} revision days
                    </span>
                  )}
                  <span className="text-[11px] font-heading px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1.5">
                    <ClipboardCheck className="w-3 h-3" /> mocks scheduled
                  </span>
                </div>
                <p className="font-body text-[12px] text-slate-500 mt-2">{plan.risk_message}</p>

                <div className="mt-3 grid grid-cols-3 sm:grid-cols-7 gap-1.5" data-testid="wizard-calendar">
                  {days.map(d => {
                    const hasMock = d.items?.some(i => i.node_type === 'mock');
                    const isFinal = d.kind === 'final_revision';
                    return (
                      <button key={d.date} onClick={() => setZoom(d.date === zoom ? '' : d.date)}
                        data-testid={`wizard-day-${d.date}`}
                        className={`rounded-2xl border p-2 text-left transition-all hover:-translate-y-0.5 ${
                          zoom === d.date ? 'ring-2 ring-indigo-400 border-indigo-300 bg-white'
                            : KIND_STYLE[d.kind] || KIND_STYLE.study}`}>
                        <p className="font-heading text-[10px] uppercase tracking-wider opacity-70">{weekday(d.date)}</p>
                        <p className="font-heading text-[13px] font-bold">{dayLabel(d.date)}</p>
                        <p className="font-body text-[10px] opacity-80">{d.minutes} min</p>
                        {(hasMock || isFinal) && (
                          <p className="font-heading text-[9px] uppercase tracking-wider mt-0.5">
                            {isFinal ? '2 hrs' : 'mock'}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {zoomDay && (
                  <div className="mt-3 rounded-3xl border-2 border-indigo-200 bg-indigo-50/50 p-4 animate-in fade-in zoom-in-95 duration-200"
                    data-testid="wizard-day-detail">
                    <p className="font-heading text-sm font-bold text-slate-900">
                      {new Date(`${zoomDay.date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                      <span className="font-body text-[12px] text-slate-500 ml-2">{zoomDay.minutes} minutes planned</span>
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {(zoomDay.items || []).map(i => (
                        <li key={i.ref} className="flex items-start gap-2 font-body text-[12px] text-slate-700">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                          <span><strong className="font-heading">{i.subject}</strong> — {i.label} · {i.minutes} min</span>
                        </li>
                      ))}
                      {!(zoomDay.items || []).length && (
                        <li className="font-body text-[12px] text-slate-500">A free day — rest, or get ahead if you feel like it.</li>
                      )}
                    </ul>
                  </div>
                )}

                <p className="font-body text-[12px] text-slate-500 mt-3">
                  You can change this whenever you like — open <strong className="font-heading">Exam</strong> from
                  your profile menu or the sidebar to edit the portion, move a day or regenerate the plan.
                </p>
              </>
            )}
          </div>
        )}

        <BuddyCorner inline size={56} mood={step === 3 ? 'happy' : 'curious'}
          line={step === 1
            ? "Tell me the exam and its dates — I'll count backwards from the paper so nothing is left to the last night."
            : step === 2
              ? "Tick only what your teacher put in the portion. I'd rather plan less and finish it than plan everything and panic."
              : "This is your routine, not a rule. Move a day whenever life happens and I'll re-plan around it."} />

        {/* ---------- footer ---------- */}
        <div className="flex items-center justify-between gap-2 mt-4">
          <Button variant="outline" size="sm" data-testid="wizard-back"
            onClick={() => (step === 1 ? onOpenChange(false) : setStep(s => s - 1))}>
            {step === 1 ? 'Not now' : <><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</>}
          </Button>
          <div className="flex items-center gap-2">
            {step === 3 && (
              <Button variant="outline" size="sm" onClick={regenerate} disabled={busy} data-testid="wizard-regenerate">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Regenerate
              </Button>
            )}
            <Button size="sm" disabled={busy} data-testid="wizard-next"
              onClick={() => (step === 1 ? goStep2() : step === 2 ? goStep3() : (onDone?.(exam, plan), onOpenChange(false)))}
              className="bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              {step === 3 ? 'Finish' : <>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExamModeWizard;
